import OktaSignIn from '@okta/okta-signin-widget';
import '@okta/okta-signin-widget/css/okta-sign-in.min.css';
import { get } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { OKTA_LOGIN_URL } from '../../common/constants';
import { useAppSelector } from '../../common/hooks';
import { addLogEvent } from '../../features/auth/authSlice';
import oktaAuth from '../../services/configs/oktaAuth';
import config from './config';
import './okta-sign-in-custom-theme.css';
import { useOktaAuth } from '@okta/okta-react';
import { Spinner } from '../../components/atoms';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

const OktaLoginWidget = ({ currentLanguage }: { currentLanguage: string }) => {
    const { authState } = useOktaAuth();
    const oktaWidgetContainerRef = useRef(null);
    const [isWidgetReady, setIsWidgetReady] = useState(false);
    // const logo = useAppSelector((state) => state?.global?.globalConfig?.logo);
    // const subdomain = useAppSelector((state) => state?.global?.globalConfig?.subdomain);
    // const tenant = useAppSelector((state) => state?.auth?.user?.tenant);
    const brandName = useAppSelector((state) => state?.global?.globalConfig?.disclaimers?.brandName);
    const theme = useTheme();
    const { t } = useTranslation();

    const {
        clientId,
        baseUrl,
        redirectUri,
        authParams: { issuer, dpop, pkce, scopes }
    } = config?.oidc;

    useEffect(() => {
        if (!oktaWidgetContainerRef.current) return;
        // Clear orphaned DPoP CryptoKeyPair keys from previous sessions.
        // Stale keys can cause malformed DPoP proofs which trigger ERR_HTTP2_PROTOCOL_ERROR.
        void oktaAuth.clearDPoPStorage(true);
        // Function to handle the OAuth response after the redirect
        async function handleOAuthResponse(res: any) {
            try {
                const tokens = get(res, 'tokens');
                void oktaAuth.handleLoginRedirect(tokens);
                if (!tokens) {
                    throw new Error('Okta authentication error');
                }
            } catch (error) {
                console.log('error in okta widget handleOAuthResponse', error);
                await addLogEvent(
                    OKTA_LOGIN_URL,
                    'response',
                    {
                        errorName: error,
                        errorType: 'Error parsing OAuth response'
                    },
                    'failed'
                );
                throw error;
            }
        }
        console.log(`${process.env.PUBLIC_URL}`, 'login widget initializing');
        // if (logo) {
        const widget = new OktaSignIn({
            baseUrl,
            clientId,
            redirectUri,
            authParams: { issuer, dpop, pkce, scopes, cookies: { sameSite: 'lax' } },
            // logo: isFranklinAdviceTenant ? FTLogo_Black : logo,
            logo: `${process.env.PUBLIC_URL}/assets/images/FTLogo_Black.webp`,
            logoText: brandName, // shows when logo fails to load.
            language: currentLanguage // shows language from tenantInfo if fails to load, defaults to English,
        });

        if (oktaWidgetContainerRef.current) {
            console.log('login widget rendered');
            widget.renderEl({ el: oktaWidgetContainerRef.current }, handleOAuthResponse, (error: any) => {
                console.log('error from okta widget renderEl', error);
                throw error;
            });
        }

        widget.on('ready', () => {
            console.log('login widget ready');
            setIsWidgetReady(true);
        });

        return () => {
            console.log('login widget removed');
            widget.remove();
        };
        // }
    }, [currentLanguage]);

    return (
        <>
            <article ref={oktaWidgetContainerRef}></article>
            {!isWidgetReady && (
                <Spinner
                    enabled={!isWidgetReady}
                    message={authState?.isAuthenticated ? t('SIGNING_SECURELY') : t('PPREPARING_SECURE_SIGNIN')}
                    messageColor={theme.palette.grey[500]}
                />
            )}
        </>
    );
};

export default OktaLoginWidget;
